<script lang="ts" setup>
import ConfirmationModal from '@/components/ConfirmationModal.vue';
import { formatAmount, formatDateFrench } from '@/utils/formatters';
import { getMarketMotifRejetLabel } from '@/utils/market';
import axios from 'axios';
import { inject, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

const BACKEND_URL = inject('BACKEND_URL');
const router = useRouter();
const commande = reactive({
    id: 0,
    num_commande: '',
    created_at: '',
    updated_at: '',
    produits: [] as any[],
    mode_paiement: '',
    statut: '',
    frais_livraison: 0,
    nb_echeance: 0,
    nb_jour_echeance: 0,
    total_echeances: 0,
    total_promotion: 0,
    reste: 0,
    sous_total: 0,
    total: 0,
    adresse_livraison_id: 0,
    type_livraison_id: 0,
    user_id: 0,
    date_livraison_prevue: '',
    date_validation_commande: '',
    date_expedition_commande: '',
    date_livraison_commande: '',
    reception_commande: false,
    date_reception_commande: '',
    motif_rejet_commande: '',
    commentaire_rejet_commande: '',
    adresse_livraison: {
        id: 0,
        adresse: '',
        telephone: '',
    }
});
const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
const loading = ref(false);
const errorMessage = ref('');

onMounted(() => {
    const routeCommande: any = router.currentRoute.value.meta.commande;
    if (routeCommande) {
        routeCommande.produits.forEach((p: any) => {
            p.quantite = parseFloat(p.quantite);
        });
        Object.assign(commande, routeCommande);
    }

});

async function doConfirmReception() {
    const confirmation = await confirmationModalRef.value?.open("Commande Market", "Confirmez-vous la réception de cette commande ?");
    if (confirmation) {
        try {
            loading.value = true;
            errorMessage.value = '';
            const result = await axios.post(`/market/confirm_reception_commande/${commande.id}`, {});
            Object.assign(commande, result.data.commande);
        } catch(error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }
}

</script>
<template>
    <!--Page--> 
    <section class="container-full-5">
        
        <h2 class="secondary-font gap-4 mt-2 mb-4">
        Ma commande
        </h2>

        <div class="s10_mobile">
            <h3 class="secondary-font text-grey-800 mb-4">#{{ commande.num_commande }}</h3>
            <!---->
            <div class="flex-center mb-6" v-for="(product, index) of commande.produits" :key="index">
                <div class="s5">
                    <a href="">
                        <div class="img-area rounded-15 mb-0 px-3 py-2">
                            <img :src="BACKEND_URL + '/assets/market_produits/300/' + product.image" width="100%" />
                        </div>
                    </a>
                </div>
                <div class="s1">&nbsp;</div>
                <div class="s14">
                    <a href="">
                        <h6 class="title secondary-font text-grey-600 mb-3">{{ product.nom }}</h6>
                    </a>
                    <h5 class="secondary-font font-bold mb-3">{{ product.quantite + ' x ' + formatAmount(product.total) }} F CFA</h5>
                </div>
            </div>
            <!---->

            <!--Stepper-->
            <!-- <div class="mb-6">
                <div class="step bg-trans">
                    <div class="step-dot active">
                        <div class="step-label">Création du compte</div>
                    </div>
                    <div class="step active">
                        <div class="step-bg"></div>
                    </div>

                    <div class="step-dot">
                        <div class="step-label">Activation du compte</div>
                    </div>
                    <div class="step">
                        <div class="step-bg"></div>
                    </div>

                    <div class="step-dot">
                        <div class="step-label">Activation de la carte</div>
                    </div>
                </div>
            </div> -->
            <!--End Stepper-->

            <hr/>
            <h3 class="secondary-font text-grey-700 mt-5 mb-5">Livraison prévue</h3>
            <h6 class="secondary-font mb-3">
                <!-- Sam. 14 juin - mar. 17 juin 2026 -->
                {{ commande.date_livraison_prevue ? formatDateFrench(commande.date_livraison_prevue) : 'Date de livraison non précisée' }}
            </h6>
            <h6 class="secondary-font mb-4 text-grey-700">
                {{ commande.adresse_livraison ? commande.adresse_livraison.adresse : 'Adresse de livraison non précisée' }}<br/>
                {{ commande.adresse_livraison ? commande.adresse_livraison.telephone : 'Téléphone non précisé' }}
            </h6>
            <div class="clear-both h-10"></div>

            <!--Stepper vertical-->
            <div class="mb-6 vertical">
                <div class="flex">
                    <div>
                        <div class="step-dot" :class="{'active': commande.statut != 'cancelled', 'danger': commande.statut == 'cancelled'}"></div>
                        <div class="step" style="height: 5rem;" v-if="commande.statut != 'cancelled'">
                            <div class="step-bg"></div>
                        </div>
                    </div>
                    <div class="px-3">
                        <div class="step-label">
                            {{ commande.date_validation_commande ? (commande.statut == 'cancelled' ? ('Commande annulée (' + getMarketMotifRejetLabel(commande.motif_rejet_commande) + ')') : 'Commande confirmée') : 'Commande en cours de validation' }}
                            <!-- Votre commande a été validée -->
                        </div>
                        <p class="step-description">
                            {{ commande.date_validation_commande ? formatDateFrench(commande.date_validation_commande) : '' }}
                            <ng-container v-if="commande.commentaire_rejet_commande">
                                <br/>
                                <span class="text-red">{{ commande.commentaire_rejet_commande }}</span>
                            </ng-container>
                        </p>
                    </div>
                </div>
                <ng-container v-if="commande.statut != 'cancelled'">
                    <div class="flex">
                        <div>
                            <div class="step-dot" :class="{ active: commande.date_validation_commande }"></div>
                            <div class="step" style="height: 5rem;">
                                <div class="step-bg"></div>
                            </div>
                        </div>
                        <div class="px-3">
                            <div class="step-label">
                                {{ commande.date_expedition_commande ? 'Commande expédiée' : 'Commande en cours de traitement' }}
                            </div>
                            <p class="step-description">
                                {{ commande.date_expedition_commande ? formatDateFrench(commande.date_expedition_commande) : '' }}
                            </p>
                        </div>
                    </div>
                    <div class="flex">
                        <div>
                            <div class="step-dot" :class="{ active: commande.date_expedition_commande }"></div>
                            <div class="step" style="height: 5rem;">
                                <div class="step-bg"></div>
                            </div>
                        </div>
                        <div class="px-3">
                            <div class="step-label">
                                {{ commande.date_livraison_commande ? 'Commande livrée' : 'Livraison commande' }}
                                
                            </div>
                            <p class="step-description">
                                {{ commande.date_livraison_commande ? formatDateFrench(commande.date_livraison_commande) : '' }}
                            </p>
                        </div>
                    </div>
                    <div class="flex">
                        <div>
                            <div class="step-dot" :class="{ active: commande.date_livraison_commande }"></div>
                        </div>
                        <div class="px-3">
                            <div class="step-label">
                                {{ commande.date_reception_commande ? 'Commande reçue' : 'Réception commande' }}
                            </div>
                            <p class="step-description">
                                {{ commande.date_reception_commande ? formatDateFrench(commande.date_reception_commande) : '' }}
                            </p>
                        </div>
                    </div>
                </ng-container>
                
            </div>
            <!--End Stepper-->
        </div>

        <div class="s1_mobile">&nbsp;</div>

        <div class="s9_mobile">
            <div class="widget s20 px-6 py-7">
                <h3 class="secondary-font text-grey-700 mb-5">Votre commande</h3>

                <h5 class="s10 secondary-font text-grey-700 mb-4">Sous-total</h5>
                <h5 class="s10 secondary-font text-grey-700 mb-4 text-right">{{ formatAmount(commande.sous_total) }} F</h5>
                <h5 class="s10 secondary-font text-red mb-4">Réduction</h5>
                <h5 class="s10 secondary-font text-red mb-4 text-right">-{{ formatAmount(commande.total_promotion) }} F</h5>
                <h5 class="s10 secondary-font text-grey-700 mb-4">Livraison</h5>
                <h5 class="s10 secondary-font text-grey-700 mb-4 text-right">{{ formatAmount(commande.frais_livraison) }} F</h5>
                <h5 class="s10 secondary-font text-grey-700 mb-4">TOTAL</h5>
                <h5 class="s10 secondary-font text-grey-700 mb-4 text-right">{{ formatAmount(commande.total) }} F</h5>

                <div class="clear-both h-0"></div>
                <div class="s20 flex-center bg-color rounded-20 px-5 pt-4 pb-3">
                    <h5 class="s10 secondary-font text-primary-color">Payer par mois</h5>
                    <h5 class="s10 secondary-font text-primary-color text-right">{{ formatAmount(commande.total_echeances) }} F</h5>
                </div>
                <div class="clear-both h-20"></div>
                <button class="btn-ultra lg w-100" @click="doConfirmReception" :disabled="loading" v-if="commande.statut == 'shipped'"><!-- loading sm tips color2 light white white2 success info warning danger-->
                    <div class="btn-show">
                        <span>&nbsp;J'ai reçu ma commande</span>
                    </div>
                    <div class="btn-loading">
                        <div class="loader-dots"></div>
                    </div>
                </button>
            </div>

            <div class="clear-both h-25"></div>
<!-- 
            <h3 class="secondary-font text-grey-700 mb-2">Paiement échelonné</h3>
            
            <div class="s20 flex-center border-color rounded-20 mt-2 px-5 pt-4 pb-3">
                <h5 class="s10 secondary-font text-grey-700">Janvier</h5>
                <h5 class="s10 secondary-font text-grey-700 text-right mr-2">255 000 F</h5>
                <img src="/public/icons/icon-valider.svg" class="size-medium mt--2" />
            </div>
            <div class="s20 flex-center border-color rounded-20 mt-2 px-5 pt-4 pb-3">
                <h5 class="s10 secondary-font text-grey-700">Février</h5>
                <h5 class="s10 secondary-font text-grey-700 text-right mr-2">255 000 F</h5>
                <img src="/public/icons/icon-valider.svg" class="size-medium mt--2" />
            </div>
            <div class="s20 flex-center rounded-20 mt-2 px-5 pt-4 pb-3">
                <h5 class="s10 secondary-font text-grey-700">Mars</h5>
                <h5 class="s10 secondary-font text-grey-700 text-right mr-2">255 000 F</h5>
                <img src="/public/icons/icon-valider.svg" class="size-medium mt--2 visibility-hidden" />
            </div>
            <div class="s20 flex-center rounded-20 mt-2 px-5 pt-4 pb-3">
                <h5 class="s10 secondary-font text-grey-700">Avril</h5>
                <h5 class="s10 secondary-font text-grey-700 text-right mr-2">255 000 F</h5>
                <img src="/public/icons/icon-valider.svg" class="size-medium mt--2 visibility-hidden" />
            </div> -->
        </div>

        <div class="clear-both h-70"></div>
    </section>
    <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
</template>